import { Alert, Box, Button, Chip, LinearProgress, Link, Stack, Tooltip, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import React, { useEffect, useState } from 'react';
import {
    conciergeAPI,
    ILearningUpdateFollowUpItem,
    ILearningUpdateStage,
    ILearningUpdateWorkKind,
} from 'api/concierge';
import { LearningUpdateRun as LearningUpdateRunBlock } from 'app/concierge/types';
import getErrorMessage from 'tools/errorMessage';
import { useQueryClient } from '@tanstack/react-query';
import useLearningUpdateStatusQuery, {
    TERMINAL_LEARNING_UPDATE_STATUSES,
    learningUpdateStatusQueryKey,
} from 'app/concierge/hooks/useLearningUpdateStatusQuery';
import {
    completedLearningUpdateCount,
    furthestReachedLearningStage,
    LEARNING_STAGES,
    learningUpdateCount,
    unfinishedEarlierLearningStage,
} from './learningUpdateProgress';

const FOLLOW_UP_NAME_LIMIT = 3;

function FollowUpLine({ item, index }: { item: ILearningUpdateFollowUpItem; index: number }) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const names = item.measures.map((measure) => measure.name);
    const joined = names.join(', ');
    let text = '';
    let linkLabel = '';
    switch (item.class) {
        case 'measure_question':
            if (names.length > 1) {
                // The backend sends one grouped item per model: one list of
                // blocked measures, one review-queue link, instead of a
                // repeated line-and-link per measure.
                return (
                    <Typography variant="body2" component="div">
                        {t('learning_update.follow_up.measure_question_group', { count: names.length })}
                        <Typography variant="body2" component="div" color="text.secondary">
                            {joined}
                        </Typography>
                        {item.resolve_url && (
                            <Link
                                href={item.resolve_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                data-test={`learning_update_follow_up_link_${index}`}
                            >
                                {t('learning_update.follow_up.answer_now')}
                            </Link>
                        )}
                    </Typography>
                );
            }
            text = t('learning_update.follow_up.measure_question', { measure: names[0] });
            linkLabel = t('learning_update.follow_up.answer_now');
            break;
        case 'date_role_question': {
            // Measure count and date count are independent, and i18next
            // pluralizes on `count` alone — so the multi-date wording is its own
            // key instead of a list smuggled into the single-date sentence.
            const dateNames = item.question?.date_names ?? [];
            text =
                dateNames.length > 1
                    ? t('learning_update.follow_up.date_role_question_dates', {
                          count: names.length,
                          measures: joined,
                          dates: dateNames.join(', '),
                          dateCount: dateNames.length,
                      })
                    : t('learning_update.follow_up.date_role_question', {
                          count: names.length,
                          measures: joined,
                          date: dateNames[0] ?? '',
                      });
            linkLabel = t('learning_update.follow_up.answer_now');
            break;
        }
        case 'no_date_axis':
        case 'no_date_relationship': {
            const prefix = `learning_update.follow_up.${item.class}`;
            text =
                names.length === 1
                    ? t(`${prefix}_single`, { measures: joined })
                    : names.length <= FOLLOW_UP_NAME_LIMIT
                      ? t(`${prefix}_list`, { measures: joined })
                      : t(`${prefix}_truncated`, { first: names[0], count: names.length - 1 });
            break;
        }
        default:
            text = t('learning_update.follow_up.needs_attention', { count: names.length, measures: joined });
            linkLabel = t('learning_update.follow_up.open_profile');
            break;
    }

    return (
        <Typography
            variant="body2"
            component="div"
            color={
                item.class === 'no_date_axis' || item.class === 'no_date_relationship'
                    ? 'text.secondary'
                    : 'text.primary'
            }
        >
            {text}
            {item.resolve_url && linkLabel && (
                <>
                    {' '}
                    <Link
                        href={item.resolve_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        data-test={`learning_update_follow_up_link_${index}`}
                    >
                        {linkLabel}
                    </Link>
                </>
            )}
        </Typography>
    );
}

const readable = (value?: string | null) => (value || '').replaceAll('_', ' ');

// The KG Profile editor routes by hash; opened in a new tab, so a plain href
// rather than SPA navigation (the /data/editor/kg-profile/ui shell handles it).
const kgProfileUrl = (modelObjectUid: string) => `/data/editor/kg-profile/ui#m/${encodeURIComponent(modelObjectUid)}`;

const workKind = (stage: ILearningUpdateStage | undefined, itemKind: string) =>
    stage?.work_kinds?.find((item) => item.item_kind === itemKind);

const serverTime = (value?: string | null) => {
    if (!value) return Number.NaN;
    const isoValue = value.replace(' ', 'T');
    const timezoneAwareValue = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(isoValue) ? isoValue : `${isoValue}Z`;

    return new Date(timezoneAwareValue).getTime();
};

const elapsed = (started?: string, finished?: string | null) => {
    if (!started) return '';
    const start = serverTime(started);
    const end = finished ? serverTime(finished) : Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return '';
    const seconds = Math.max(0, Math.floor((end - start) / 1000));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export default function LearningUpdateRun({ block }: { block: LearningUpdateRunBlock }) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const [runUid, setRunUid] = useState(block.run_uid);
    const [actionError, setActionError] = useState('');
    const [busy, setBusy] = useState(false);
    const [showDiagnostics, setShowDiagnostics] = useState(false);
    const queryClient = useQueryClient();

    const { data: status, error: queryError } = useLearningUpdateStatusQuery(runUid);
    const displayedError =
        actionError || (queryError ? getErrorMessage(queryError) || t('learning_update.load_failed') : '');

    useEffect(() => {
        setActionError('');
    }, [status]);

    const cancel = async () => {
        setBusy(true);
        try {
            await conciergeAPI.cancelLearningUpdate(runUid);
            await queryClient.invalidateQueries({ queryKey: learningUpdateStatusQueryKey(runUid) });
        } catch (requestError: unknown) {
            setActionError(getErrorMessage(requestError) || t('learning_update.cancel_failed'));
        } finally {
            setBusy(false);
        }
    };

    const retry = async () => {
        setBusy(true);
        try {
            const result = await conciergeAPI.retryLearningUpdate(runUid);
            if (result.run_uid !== runUid) {
                setRunUid(result.run_uid);
            } else {
                await queryClient.invalidateQueries({ queryKey: learningUpdateStatusQueryKey(runUid) });
            }
        } catch (requestError: unknown) {
            setActionError(getErrorMessage(requestError) || t('learning_update.retry_failed'));
        } finally {
            setBusy(false);
        }
    };

    if (!status) {
        return (
            <Box sx={{ width: '100%', py: 1 }}>
                <Typography variant="body2">{t('learning_update.connecting')}</Typography>
                <LinearProgress sx={{ mt: 1 }} />
                {displayedError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {displayedError}
                    </Alert>
                )}
            </Box>
        );
    }

    const run = status.run;
    const isTerminal = TERMINAL_LEARNING_UPDATE_STATUSES.includes(run.status);
    const isRetryable = run.status === 'partial' || run.status === 'failed';
    const heartbeatAge = run.heartbeat_time
        ? Math.max(0, Math.floor((Date.now() - serverTime(run.heartbeat_time)) / 1000))
        : null;
    const delayed = status.progress.active && heartbeatAge !== null && heartbeatAge > 60;
    const stageDetails = (stage?: string | null) => {
        const normalizedStage = stage && LEARNING_STAGES.includes(stage) ? stage : LEARNING_STAGES[0];
        const stageIndex = LEARNING_STAGES.indexOf(normalizedStage);

        return {
            label: t(`learning_update.stage.${normalizedStage}`),
            number: stageIndex + 1,
            total: LEARNING_STAGES.length,
        };
    };
    const progressStageName = furthestReachedLearningStage(run.current_stage, status.stages);
    const currentStage = stageDetails(progressStageName);
    const currentStageCounts = status.stages.find((stage) => stage.stage === progressStageName);
    const unfinishedEarlierStage = unfinishedEarlierLearningStage(progressStageName, status.stages);
    const stageDetail = (() => {
        switch (progressStageName) {
            case 'initialize':
                return t('learning_update.stage_detail.initialize');
            case 'snapshot':
                return t('learning_update.stage_detail.snapshot');
            case 'metadata_nodes': {
                const chunks = workKind(currentStageCounts, 'metadata_batch');
                return chunks
                    ? t('learning_update.stage_detail.metadata_nodes', {
                          completed: completedLearningUpdateCount(chunks),
                          total: learningUpdateCount(chunks.planned_count),
                      })
                    : '';
            }
            case 'edge_reconciliation':
                return status.models.length > 1
                    ? t('learning_update.stage_detail.edge_reconciliation', { count: status.models.length })
                    : '';
            case 'node_contexts': {
                const fields = workKind(currentStageCounts, 'node_context');
                const dimensions = workKind(currentStageCounts, 'dimension_profile');
                if (!fields) return '';
                if (!dimensions) {
                    return t('learning_update.stage_detail.node_contexts_fields', {
                        completed: completedLearningUpdateCount(fields),
                        total: learningUpdateCount(fields.planned_count),
                    });
                }
                return t('learning_update.stage_detail.node_contexts', {
                    fieldsCompleted: completedLearningUpdateCount(fields),
                    fieldsTotal: learningUpdateCount(fields.planned_count),
                    dimensionsCompleted: completedLearningUpdateCount(dimensions),
                    dimensionsTotal: learningUpdateCount(dimensions.planned_count),
                });
            }
            case 'semantic_enrichment': {
                const kinds = ['measure_semantic', 'dimension_learning', 'date_learning']
                    .map((kind) => workKind(currentStageCounts, kind))
                    .filter(Boolean) as ILearningUpdateWorkKind[];
                const total = kinds.reduce((sum, item) => sum + learningUpdateCount(item.planned_count), 0);
                const completed = kinds.reduce((sum, item) => sum + completedLearningUpdateCount(item), 0);
                return total ? t('learning_update.stage_detail.semantic_enrichment', { completed, total }) : '';
            }
            case 'associations_and_conflicts': {
                if (status.models.length <= 1) return '';
                const dates = workKind(currentStageCounts, 'date_reconciliation');
                const glossary = workKind(currentStageCounts, 'glossary_reconciliation');
                return dates || glossary
                    ? t('learning_update.stage_detail.associations_and_conflicts', {
                          datesCompleted: completedLearningUpdateCount(dates),
                          datesTotal: learningUpdateCount(dates?.planned_count),
                          glossaryCompleted: completedLearningUpdateCount(glossary),
                          glossaryTotal: learningUpdateCount(glossary?.planned_count),
                      })
                    : '';
            }
            case 'clarification_consolidation': {
                if (status.models.length <= 1) return '';
                const models = workKind(currentStageCounts, 'clarification_consolidation');
                return models
                    ? t('learning_update.stage_detail.clarification_consolidation', {
                          completed: completedLearningUpdateCount(models),
                          total: learningUpdateCount(models.planned_count),
                      })
                    : '';
            }
            case 'finalization': {
                const models = workKind(currentStageCounts, 'model_finalization');
                const report = workKind(currentStageCounts, 'report_finalization');
                if (
                    status.models.length > 1 &&
                    models &&
                    completedLearningUpdateCount(models) < learningUpdateCount(models.planned_count)
                ) {
                    return t('learning_update.stage_detail.finalization', {
                        completed: completedLearningUpdateCount(models),
                        total: learningUpdateCount(models.planned_count),
                    });
                }
                return report && completedLearningUpdateCount(report) < learningUpdateCount(report.planned_count)
                    ? t('learning_update.stage_detail.finalizing_run')
                    : '';
            }
            default:
                return '';
        }
    })();
    const activity = status.activity;
    const activityDetail = (() => {
        if (isTerminal || !activity || !activity.total) return '';
        const values = { ...activity.parameters, current: activity.current, total: activity.total };
        switch (activity.message_key) {
            case 'learning.selector_dates_progress':
                return t('learning_update.activity.selector_dates_progress', values);
            default:
                return t('learning_update.activity.generic', values);
        }
    })();
    const nodeSummary = status.knowledge_graph_nodes;
    const profileModels = status.models.filter((model) => (model.model_object_uid || '').trim() !== '');
    const stageProgress = isTerminal ? 100 : Math.max(4, ((currentStage.number - 1) / currentStage.total) * 100);
    const activeStatus =
        run.status === 'waiting_for_model'
            ? t('learning_update.waiting_for_model')
            : run.status === 'cancelling'
              ? t('learning_update.cancelling')
              : t('learning_update.working');
    const completionCopy =
        run.status === 'succeeded'
            ? run.source_unchanged
                ? t('learning_update.no_model_changes')
                : t('learning_update.completed')
            : run.status === 'partial'
              ? t('learning_update.completed_with_issues')
              : run.status === 'failed'
                ? t('learning_update.failed')
                : run.status === 'cancelled'
                  ? t('learning_update.cancelled')
                  : run.status === 'waiting_for_model'
                    ? t('learning_update.waiting_for_model')
                    : run.status === 'cancelling'
                      ? t('learning_update.cancelling')
                      : run.retry_count > 0
                        ? t('learning_update.retrying_title')
                        : t('learning_update.in_progress');

    return (
        <Box sx={{ width: '100%', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, my: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">{completionCopy}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {run.resource_name || t('learning_update.report')} ·{' '}
                        {isTerminal
                            ? t('learning_update.finished_in', {
                                  duration: elapsed(run.started_time, run.finished_time),
                              })
                            : elapsed(run.started_time, run.finished_time)}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    {!isTerminal && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={busy}
                            onClick={cancel}
                            data-test="learning_update_cancel_button"
                        >
                            {t('learning_update.cancel')}
                        </Button>
                    )}
                    {isRetryable && (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={retry}
                            startIcon={<IconMi icon="refresh-cw" />}
                            data-test="learning_update_retry_button"
                        >
                            {t('learning_update.retry_failed_items')}
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={stageProgress}
                    color={run.status === 'failed' ? 'error' : run.status === 'partial' ? 'warning' : 'primary'}
                    sx={{ flex: 1, height: 6, borderRadius: 1 }}
                />
                {!isTerminal && (
                    <Typography variant="caption" sx={{ minWidth: 90, textAlign: 'right', fontWeight: 600 }}>
                        {activeStatus}
                    </Typography>
                )}
            </Stack>

            {!isTerminal && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                        {t('learning_update.stage_progress', {
                            stage: currentStage.label,
                            current: currentStage.number,
                            total: currentStage.total,
                        })}
                    </Typography>
                    {stageDetail && (
                        <Typography variant="caption" color="text.secondary">
                            {stageDetail}
                        </Typography>
                    )}
                    {activityDetail && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            data-test="learning_update_activity_detail"
                        >
                            {activityDetail}
                        </Typography>
                    )}
                    {unfinishedEarlierStage && (
                        <Typography variant="caption" color="text.secondary" component="div">
                            {t('learning_update.finishing_earlier_stage', {
                                stage: stageDetails(unfinishedEarlierStage).label,
                            })}
                        </Typography>
                    )}
                </Box>
            )}

            {isTerminal && nodeSummary?.available && (
                <Box sx={{ mt: 1.25 }}>
                    <Typography variant="subtitle2">{t('learning_update.knowledge_graph_nodes')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('learning_update.knowledge_graph_node_totals', {
                            total: nodeSummary.total,
                            created: nodeSummary.created,
                            updated: nodeSummary.updated,
                        })}
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                        {(['measure', 'dimension', 'date'] as const).map((nodeType) => (
                            <Chip
                                key={nodeType}
                                size="small"
                                variant="outlined"
                                label={t('learning_update.knowledge_graph_node_type', {
                                    type: t(`learning_update.node_type.${nodeType}`),
                                    created: nodeSummary.by_type[nodeType].created,
                                    updated: nodeSummary.by_type[nodeType].updated,
                                })}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {isTerminal && status.follow_up?.available && status.follow_up.items.length > 0 && (
                <Box sx={{ mt: 1.25 }} data-test="learning_update_follow_up">
                    <Typography variant="subtitle2">{t('learning_update.follow_up.header')}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                        {t('learning_update.follow_up.summary')}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {status.follow_up.items.map((item, index) => (
                            <FollowUpLine
                                key={`${item.class}-${item.model_name}-${item.measures[0]?.data_measure_id ?? index}`}
                                item={item}
                                index={index}
                            />
                        ))}
                    </Stack>
                    {isRetryable && status.follow_up.items.some((item) => item.resolve_url) && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
                            {t('learning_update.follow_up.after_answering')}
                        </Typography>
                    )}
                </Box>
            )}

            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {run.created_count > 0 && (
                    <Chip size="small" label={t('learning_update.created', { count: run.created_count })} />
                )}
                {run.updated_count > 0 && (
                    <Chip size="small" label={t('learning_update.updated', { count: run.updated_count })} />
                )}
                {run.unchanged_count > 0 && (
                    <Chip size="small" label={t('learning_update.unchanged', { count: run.unchanged_count })} />
                )}
                {run.partial_count > 0 && (
                    <Chip
                        size="small"
                        color="warning"
                        label={t('learning_update.partial', { count: run.partial_count })}
                    />
                )}
                {run.failed_count > 0 && (
                    <Chip size="small" color="error" label={t('learning_update.errors', { count: run.failed_count })} />
                )}
                {run.retry_count > 0 && (
                    <Chip size="small" label={t('learning_update.retrying', { count: run.retry_count })} />
                )}
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                {isTerminal &&
                    profileModels.map((model, index) => (
                        <Link
                            key={model.model_object_uid}
                            href={kgProfileUrl(model.model_object_uid)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontSize: '0.8125rem' }}
                            data-test={`learning_update_profile_link_${index}`}
                        >
                            {profileModels.length === 1
                                ? t('learning_update.open_profile')
                                : t('learning_update.open_profile_for', {
                                      name: model.display_name || model.model_object_uid,
                                  })}
                        </Link>
                    ))}
                <Link
                    href={`/editor/graph/monitor/${runUid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ fontSize: '0.8125rem' }}
                    data-test="learning_update_monitor_link"
                >
                    {t('learning_update.open_monitor')}
                </Link>
            </Stack>

            {delayed && (
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
                    <Tooltip title={t('learning_update.delayed_help')}>
                        <Chip size="small" color="warning" label={t('learning_update.delayed')} />
                    </Tooltip>
                </Stack>
            )}
            {status.failures.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => setShowDiagnostics((value) => !value)}
                        startIcon={<IconMi icon={showDiagnostics ? 'chevron-up' : 'chevron-down'} />}
                        data-test="learning_update_diagnostics_toggle_button"
                    >
                        {showDiagnostics
                            ? t('learning_update.hide_diagnostics')
                            : t('learning_update.show_diagnostics', { count: status.failures.length })}
                    </Button>
                    {showDiagnostics && (
                        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                            {status.failures.map((failure) => {
                                const failureStage = String(failure.stage || '');
                                const failureLabel = LEARNING_STAGES.includes(failureStage)
                                    ? stageDetails(failureStage).label
                                    : readable(String(failure.item_kind || 'learning update item'));

                                return (
                                    <Alert
                                        severity={failure.status === 'failed' ? 'error' : 'warning'}
                                        key={String(failure.work_uid)}
                                    >
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                                            {failureLabel}
                                        </Typography>
                                        <Typography variant="caption" component="div">
                                            {String(
                                                failure.error_message ||
                                                    failure.result_summary ||
                                                    failure.result_code ||
                                                    failure.status,
                                            )}
                                        </Typography>
                                    </Alert>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            )}
            {displayedError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {displayedError}
                </Alert>
            )}
        </Box>
    );
}
